import URL from "url-parse";
import { Injectable, OnDestroy } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { UtilityService } from "../../app/_core/services/utility.service";
import { filter, takeUntil, distinctUntilChanged } from "rxjs/operators";
import { PageViewData } from "../models/page-view-data";
import { DefaultValues } from "../models/default-values";
import { Constants } from "../models/constants";
import { LocalStorageService } from "@dodo/local-storage";
import { LocalStorageKeys } from "../../app/_core/services/local-storage-state-restor.service";
import { ProductDetails } from "../models/product_details";
import { Subject } from "rxjs";
import { Auth0User } from "app/_core/services/models/auth0-user.model";
import { BoboPaymentService } from "app/bobo/bobo-payment-service";
import { cartSelector, CartState, PlanState } from "app/_store";
import { isMobile, isOrderESIM, isAuthenticatedOrdereSIM } from "app/shared";
import { planMobileReceiptSelector } from "app/_store/plan/plan.selector";
import moment from "moment";
import { DodoError } from "app/shared/models/error.model";
import { SimStatusResponse } from "app/_core/_models/mobile/mobile.models";

declare global {
	interface Window {
		utag: any;
		utag_data: any;
		utag_cfg_ovrd: any;
		opera: any;
		global: any;
	}
}

@Injectable({
	providedIn: "root",
})
export class TealiumService implements OnDestroy {
	private isMobileFlow: boolean;
	private addToCartTriggered: boolean;
	private customerAddress: any; // Zeus.Address;
	private customerAddress_e: any; // Zeus.Address;
	private checkoutUserData: any;
	private sales_id: string;
	private cartData: any;
	private order_total: number;
	private order_subtotal: number;

	private pageViewData = new PageViewData();
	private previousPagePath: string;
	private currentPagePath: string;

	private lastProductView: string;
	private internetProductDetails = new ProductDetails();
	private mobileProductDetails = new ProductDetails();
	private energyProductDetails = new ProductDetails();
	private electricityProductDetails = new ProductDetails();
	private gasProductDetails = new ProductDetails();

	private dealer: Auth0User;
	private checkoutError: any;

	private _unsubscribeAll: Subject<any>;
	private _refId: any;

	private static formatDataForTealium(
		object,
		defaultValue = "",
		convertToLowerCase = false
	) {
		if (typeof object === "undefined" || object === null) {
			return {};
		}
		if (Array.isArray(object)) {
			return object.map((obj) =>
				this.formatDataForTealium(obj, defaultValue, convertToLowerCase)
			);
		}
		if (typeof object !== "object") {
			return this.formatValueForTealium(
				object,
				defaultValue,
				convertToLowerCase
			);
		}
		Object.keys(object).forEach(
			(key) => object[key] == null && delete object[key]
		);
		return Object.keys(object).reduce((newObj, key) => {
			const newVal = TealiumService.formatDataForTealium(
				object[key],
				defaultValue,
				convertToLowerCase
			);
			newObj[key] = newVal;
			return newObj;
		}, {});
	}

	private static formatValueForTealium(
		value,
		defaultValue = "",
		convertToLowerCase = false
	) {
		if (Array.isArray(value)) {
			return value.map((obj) =>
				this.formatValueForTealium(
					obj,
					defaultValue,
					convertToLowerCase
				)
			);
		}
		if (typeof value === "boolean") {
			return value ? "1" : "0";
		}
		if (typeof value === "undefined" || value === null) {
			return defaultValue;
		}
		let formattedValue = "";
		if (typeof value === "object") {
			return JSON.stringify(value);
		} else {
			formattedValue = value.toString().trim();
			if (formattedValue === "") {
				return defaultValue;
			}
			if (convertToLowerCase) {
				return formattedValue.toLowerCase();
			}
		}
		return formattedValue;
	}

	constructor(
		private _localStorageService: LocalStorageService,
		private cartStore: Store<CartState>,
		private planStore: Store<PlanState>,
		private utility: UtilityService,
		private __boboPaymentService: BoboPaymentService
	) {
		window.global = window;
		// this.store = store;
		this.init();
	}

	public init() {
		this.addToCartTriggered = false;

		this.internetProductDetails.category = Constants.category_broadband;
		this.internetProductDetails.id = null;
		this.mobileProductDetails.category = Constants.category_mobile;
		this.mobileProductDetails.subcategory = Constants.subcategory_postpaid;
		this.mobileProductDetails.id = null;
		this.energyProductDetails.category = Constants.category_energy;
		this.electricityProductDetails.category = Constants.category_energy;
		this.electricityProductDetails.subcategory =
			Constants.subcategory_electricity;
		this.electricityProductDetails.id = null;
		this.gasProductDetails.category = Constants.category_energy;
		this.gasProductDetails.subcategory = Constants.subcategory_gas;
		this.gasProductDetails.id = null;

		this._unsubscribeAll = new Subject();
		this.subscribeToCheckout();
		this.subscribeToPaymentReference();

		this.errorDetails();
	}

	private subscribeToPaymentReference() {
		this.__boboPaymentService.paymentReference$
			.pipe(filter(ref => !!ref), takeUntil(this._unsubscribeAll))
			.subscribe(ref => {
				this._refId = ref;
			});
	}

	private __bundleDiscount() {
		const checkbox_state =
			this._localStorageService.getFromSessionStorage("checkbox_state");
		const checkbox_state_value =
			this._localStorageService.getFromSessionStorage(
				"checkbox_state_value"
			);

		if (
			checkbox_state !== undefined &&
			checkbox_state_value !== undefined
		) {
			this.pageViewData.checkbox_state = checkbox_state;
			this.pageViewData.checkbox_state_value = checkbox_state_value;
		}
	}

	bundleDiscountUpdate(checkState: string, checkValue: string) {
		this.trackPageEvent({
			checkbox_state: checkState,
			checkbox_state_value: checkValue,
			multi_product_checkbox_state: checkState,
		});
	}

	private subscribeToCheckout() {
		const checkoutData = JSON.parse(this._localStorageService.getFromSessionStorage("checkout"));
		this.checkoutUserData = checkoutData?.steps;
	}

	errorDetails() {
		// this.store
		// 	.select(errorSelector)
		// 	.pipe(takeUntil(this._unsubscribeAll))
		// 	.subscribe((error: DodoError) => {
		// 		if (error) {
		// 			this.internetProductDetails.errorCode = error.errorCode;
		// 			this.internetProductDetails.errorDescription = error.title;
		// 		}
		// 	});

		// this._checkoutErrorService.getPlanError().pipe(takeUntil(this._unsubscribeAll)).subscribe(err$ => {
		// 	this.checkoutError = err$;
		// });
	}

	addressSelected(pickedAddress) {
		if (!pickedAddress) {
			return;
		}

		this.trackPageEvent({
			tealium_event: "address_search",
			customer_address: pickedAddress.Address || pickedAddress.composite,
			customer_gnafid: pickedAddress.GNAFId || pickedAddress.gnafId,
		});
	}

	addToCartActive() {
		if (!this.addToCartTriggered) {
			this.trackPageEvent({
				tealium_event: "add_to_cart_button_active",
			});
			this.addToCartTriggered = true;
		}
	}

	validateSim(validationStatus: string) {
		this.trackPageEvent({
			tealium_event: "sales_funnel_interaction",
			interaction_type: "button",
			link_label: validationStatus,
			event_trigger: "sim step"
		});
	}

	mobilePlanUpdate(tealiumInfo) {
		this.trackPageEvent(tealiumInfo);
	}

	addToCart() {
		this.trackPageEvent({
			tealium_event: "cart_add",
		});
	}

	checkbox(salesFunnelStep: string, checkboxState: boolean) {
		this.trackPageEvent({
			sales_funnel_step: salesFunnelStep,
			checkbox_state: checkboxState ? "true" : "false",
		});
	}

	trackCrossSell(product: string, value: string) {
		this.trackPageEvent({
			tealium_event: "cross_sell_selection",
			selection_type: product,
			selection_value: value,
		});
	}

	offerUpdate(offer: string) {
		this.trackPageEvent({
			sales_funnel_step: "offer_update",
			checkbox_state_value: offer ? offer : " ",
		});
	}

	reportError(error: DodoError) {
		this.trackPageEvent({
			tealium_event: "error_page",
			error_code: error.errorCode,
			error_description: error.title,
		});
	}

	eventTriggered(tealiumEventData) {
		this.trackPageEvent(
			Object.assign(
				{
					tealium_event: "sales_funnel_interaction",
				},
				tealiumEventData
			)
		);
	}

	reportPayment(report) {
		this.trackPageEvent(report);
	}

	paymentSmsSent(referenceId: string) {
		this.trackPageEvent({
			event_trigger: "Payment SMS sent",
			tealium_event: "payment_sms_sent",
			dealer_reference_id: referenceId
		});
	}

	reportBoboPaymentVerificationError(error: string, code: string) {
		this.trackPageEvent({
			tealium_event: "flash_message",
			event_trigger: "error",
			error_code: code,
			error_description: error
		});
	}

	// The logic of this is to add page view data to utag_data so data can persist across page views to make reporting simpler in analytics
	trackPageView(path) {
		if (!path) {
			return;
		}
		this.dealer =
			this._localStorageService.getFromLocalStorage(
				LocalStorageKeys.dealer
			) ||
			this._localStorageService.getFromLocalStorage(
				LocalStorageKeys.kiosk
			);
		// Reset the page view data as this is a new page
		this.pageViewData = new PageViewData();

		// Track the previous page path to enable better user flow tracking in analytics
		this.previousPagePath = this.currentPagePath || document.referrer;
		this.currentPagePath =
			window.location.origin + window.location.pathname;
		this.pageViewData.previous_page_path = this.previousPagePath;

		// Sales ID
		const saleIdToken =
			this._localStorageService.getFromSessionStorage("saleId-token");
		this.sales_id = saleIdToken ? saleIdToken["salesId"] : this.sales_id;

		let cartResumeUrl = null;
		if (this.sales_id) {
			cartResumeUrl = window.location.protocol + "//";
			cartResumeUrl = cartResumeUrl + window.location.host + "/sale";
			cartResumeUrl = cartResumeUrl + "?sid=" + this.sales_id;
			if (saleIdToken) {
				cartResumeUrl = cartResumeUrl + "&tok=" + saleIdToken["token"];
				cartResumeUrl = cartResumeUrl + "&key=" + saleIdToken["token"];
			}
		}
		this.pageViewData.cart_resume_url = cartResumeUrl;

		try {
			// Clean the path passed in
			const url = new URL(path);
			const urlParts = path.split("/");

			// Set product
			urlParts.forEach((element) => {
				switch (element) {
					case "mobile":
						this.lastProductView = element;
						this.pageViewData = Object.assign(
							this.pageViewData,
							this.getProductData(
								this.__getProductDetail(this.lastProductView)
							)
						);
						break;
					case "activate":
						this.lastProductView = "mobile";
						this.pageViewData = Object.assign(
							this.pageViewData,
							this.getProductData(
								this.__getProductDetail(this.lastProductView)
							)
						);
						break;
					case "checkout":
						let _mobileDetails;
						this.cartStore
						.pipe(
							select(cartSelector),
							distinctUntilChanged(
								(p, c) => JSON.stringify(p) === JSON.stringify(c)
							),
						)
						.subscribe((p) => {
							this.isMobileFlow = isMobile(p.products?.currentProduct);
							_mobileDetails = this.isMobileFlow
								? p.products[p.products?.currentProduct].details
								: "";

							if (_mobileDetails.monthlyCost) {
								this.order_total =
								_mobileDetails.dueToday ===
									0
									? +_mobileDetails.monthlyCost
									: +_mobileDetails.dueToday;
							}
							this.order_subtotal = ProductDetails.beforeTax(
								this.order_total
							);
							Object.assign(
								this.pageViewData,
								this.cartData ||
								this._localStorageService.getFromSessionStorage(
									Constants.session_storage_key
								)
							);

							Object.assign(this.pageViewData, {
								billing_frequency: DefaultValues.billing_frequency,
								product_category:
									this.pageViewData.cart_products_category,
								product_subcategory:
									this.pageViewData.cart_products_subcategory,
							});
						});
						break;
				}
			});

			// The very important "reseller id"
			this.pageViewData.reseller_id =
				this._localStorageService.getFromSessionStorage(
					LocalStorageKeys.resellerId
				);
			// Dealer portal
			if (this.dealer) {
				this.pageViewData.dealer_name = this.dealer.dealerName;
			}

			// Set data that should be captured for all pages as soon as it exists
			if (this.checkoutUserData && this.checkoutUserData.aboutYou) {
				this.pageViewData.customer_first_name =
					this.checkoutUserData.aboutYou.firstName;
				this.pageViewData.customer_last_name =
					this.checkoutUserData.aboutYou.lastName;
				this.pageViewData.customer_dob =
					this.checkoutUserData.aboutYou.dob;
				this.pageViewData.customer_phone =
					this.checkoutUserData.aboutYou.mobile;
				this.pageViewData.customer_email =
					this.checkoutUserData.aboutYou.email;
			}

			// why??
			this.customerAddress =
				this.customerAddress_e ||
				// this._persisCartService.persistAddress ||
				this.customerAddress;
			if (this.customerAddress) {
				this.pageViewData.customer_address =
					this.customerAddress.Composite ||
					this.customerAddress.composite;
				this.pageViewData.customer_city =
					this.customerAddress.Suburb || this.customerAddress.suburb;
				this.pageViewData.customer_state =
					this.customerAddress["state"] ||
					this.customerAddress.StateName ||
					this.customerAddress.stateName;
				this.pageViewData.customer_postcode =
					this.customerAddress.Postcode ||
					this.customerAddress.postcode;
				this.pageViewData.customer_country =
					DefaultValues.customer_country;
				this.pageViewData.customer_gnafid =
					this.customerAddress?.GNAF?.GNAFId;
			}

			// Add Product Category to abandoned cart URL
			if (
				this.pageViewData.cart_resume_url &&
				this.pageViewData.cart_products_category
			) {
				this.pageViewData.cart_resume_url =
					this.pageViewData.cart_resume_url
						.concat("&pc=")
						.concat(this.pageViewData.cart_products_category[0]);
			}

			// Add page specific data depending on the step in the user flow
			// If no value is to be passed but it's a required field for the page, set the value to an empty string
			switch (this.utility.get(() => url.pathname.toLowerCase())) {
				case "/activate":
					this.pageViewData.page_id = Constants.page_id_activate_sim;
					this.pageViewData.tealium_event = 'sales_funnel';
					this.pageViewData.event_trigger = 'Signup Page View';
					break;
				case "/plan/mobile":
					this.pageViewData.page_id = Constants.page_id_mobile_plan;
					this.pageViewData.tealium_event = 'sales_funnel_product';
					this.pageViewData.event_trigger = 'Dodo Mobile Plan';
					const customerEmail = this._localStorageService.getFromSessionStorage("customer-email");
					if (customerEmail) {
						this.pageViewData.customer_email = customerEmail;
					}
					const leadId = this._localStorageService.getFromSessionStorage("leadId");
					if (leadId) {
						this.pageViewData.leadId = leadId;
					}
					const categoryName = this._localStorageService.getFromSessionStorage("categoryName");
					if (categoryName) {
						this.pageViewData.categoryName = categoryName;
					}
					const product_sim_1P = this._localStorageService.getFromSessionStorage("product_sim_1P");
					if (product_sim_1P) {
						this.pageViewData.product_sim_1P = product_sim_1P;
					}
					const product_sim_number = this._localStorageService.getFromSessionStorage("product_sim_number");
					if (product_sim_number) {
						this.pageViewData.product_sim_number = product_sim_number;
					}
					const product_promo_code = this._localStorageService.getFromSessionStorage("product_promo_code");
					if (product_promo_code) {
						this.pageViewData.product_promo_code = product_promo_code;
					}
					const salesforce_lead_id = this._localStorageService.getFromSessionStorage("salesforce_lead_id");
					if (salesforce_lead_id) {
						this.pageViewData.salesforce_lead_id = salesforce_lead_id;
					}
					const activate = JSON.parse(this._localStorageService.getFromSessionStorage("activate"));
					if (activate?.simStatus) {
						const simStatus: SimStatusResponse = activate?.simStatus; 
						this.pageViewData.retail_vendor = simStatus?.product?.productCharacteristics?.find(x => x.name === "retailerName")?.value;
						this.pageViewData.retail_store_ID = simStatus?.product?.productCharacteristics?.find(x => x.name === "storeId")?.value;
						this.pageViewData.sim_number = simStatus?.product?.productCharacteristics?.find(x => x.name === "serialNumber")?.value;
						this.pageViewData.sim_value = simStatus?.remainingValue?.amount;
					}
					break;
				case "/checkout/about-you":
				case "/order-sim":
					this.pageViewData.page_id =
						Constants.page_id_cart_about_you;
					this.pageViewData.tealium_event = 'sales_funnel_begin';
					this.pageViewData.event_trigger = 'Initiate Cart';
					this.populateCartProductsData();

					const planData = JSON.parse(this._localStorageService.getFromSessionStorage(LocalStorageKeys.plan));
					const cartData = JSON.parse(this._localStorageService.getFromSessionStorage("cart"));
					const productDiscount = planData?.mobile?.selectedPlan?.discount;
					const discountValue = productDiscount ? (productDiscount.isData ? productDiscount.data : productDiscount.isPercent ? productDiscount.percentage : productDiscount.isFixedAmount ? productDiscount.fixedAmount : 0) : undefined;
					const productId = planData ? planData.mobile.productOffering[0].productOffering[0].id : undefined;
					const promoCode = this._localStorageService.getFromSessionStorage(LocalStorageKeys.promoDiscountCode);

					this.mobilePlanUpdate({
						billing_frequency: 'Monthly',
						event_trigger: "Add To Cart",
						product_discount: [discountValue],
						product_id: productId,
						product_name: this.pageViewData.cart_products_name?.[0],
						product_original_price: [cartData?.products?.mobile?.monthlyCost],
						product_price: [cartData?.products?.mobile?.dueToday],
						product_promo_code: [promoCode],
						product_quantity: ["1"],
						sales_funnel_step: "Add To Cart",
						tealium_event: "cart_add",
					});
					break;
				case "/checkout/setup":
					this.pageViewData.page_id =
						Constants.page_id_cart_service_setup;
					this.pageViewData.tealium_event = 'sales_funnel_checkout';
					this.pageViewData.event_trigger = 'Signup Page View';
					this.populateCartProductsData();
					this.pageViewData.salesforce_lead_id =
						this._localStorageService.getFromSessionStorage(
							LocalStorageKeys.salesforceLeadId
						);

					const checkoutData = JSON.parse(this._localStorageService.getFromSessionStorage("checkout"));
					if (checkoutData && checkoutData?.steps?.aboutYou) {
						const customerData = checkoutData?.steps?.aboutYou;

						this.customerAddress = customerData.mobileAddress;
						this.pageViewData.customer_address = customerData.mobileAddressValue.composite;
						this.pageViewData.customer_dob = customerData.dob;
						this.pageViewData.customer_first_name = customerData.firstName;
						this.pageViewData.customer_last_name = customerData.lastName;
						this.pageViewData.customer_phone = customerData.mobile;
						this.pageViewData.customer_street_address = customerData.mobileAddress;
					}
					break;
				case "/checkout/payment-details":
					this.pageViewData.page_id =
						Constants.page_id_cart_payment_details;
					this.pageViewData.tealium_event = 'sales_funnel_checkout';
					this.pageViewData.event_trigger = 'Signup Page View';
					this.populateCartProductsData();

					this.pageViewData.salesforce_lead_id =
						this._localStorageService.getFromSessionStorage(
							LocalStorageKeys.salesforceLeadId
						);

					const paymentCheckoutData = JSON.parse(this._localStorageService.getFromSessionStorage("checkout"));
					if (paymentCheckoutData && paymentCheckoutData?.steps?.aboutYou) {
						const customerData = paymentCheckoutData?.steps?.aboutYou;
						const addressValue = customerData.mobileAddressValue;

						this.pageViewData.customer_dob = customerData.dob;
						this.pageViewData.customer_email = customerData.email;
						this.pageViewData.customer_first_name = customerData.firstName;
						this.pageViewData.customer_last_name = customerData.lastName;
						this.pageViewData.customer_phone = customerData.mobile;
						this.pageViewData.customer_phone_local = customerData.mobile;

						if (addressValue) {
							this.pageViewData.customer_address = addressValue.composite;
							this.pageViewData.customer_city = addressValue.suburb;
							this.pageViewData.customer_state = addressValue.state;
							this.pageViewData.customer_postcode = addressValue.postcode;
							this.pageViewData.customer_country = DefaultValues.customer_country;
							this.pageViewData.customer_street_address = customerData.mobileAddress;
							if (addressValue.gnafId) {
								this.pageViewData.customer_gnafid = addressValue.gnafId;
							}
						}
					}
					this.__bundleDiscount();
					break;
				case "/checkout/make-payment":
					this.pageViewData.page_id =
						Constants.page_id_cart_payment_summary;
					break;
				// Disabled as part of - OPET-46587 - Tealium Purchase Event Fix - Mobile
				// Moved to thank-you page
				// TODO: remove in future release once business satisfied with changes
				// case "/thank-you":
				// 	this.pageViewData.tealium_event = 'order_confirmation_view';
				// 	this.pageViewData.event_trigger = this.isMobileFlow ? 'Signup Confirmation' : 'Dodo Mobile SIM Order';
				// 	this.populateCartProductsData();
				// 	const promoCodeData = this._localStorageService.getFromSessionStorage("promo-discount-code");
				// 	if (promoCodeData) {
				// 		this.pageViewData.order_promo_code = promoCodeData;
				// 	}

				// 	this.planStore
				// 		.pipe(
				// 			select(planMobileReceiptSelector),
				// 		)
				// 	.subscribe((receiptNumber: any) => {
				// 		this.pageViewData.order_id = receiptNumber;
				// 	});

				// 	const thankyouCheckoutData = JSON.parse(this._localStorageService.getFromSessionStorage("checkout"));
				// 	if (thankyouCheckoutData && thankyouCheckoutData?.steps?.aboutYou) {
				// 		const customerData = thankyouCheckoutData?.steps?.aboutYou;
				// 		const addressValue = customerData.mobileAddressValue;

				// 		this.pageViewData.customer_dob = customerData.dob;
				// 		this.pageViewData.customer_email = customerData.email;
				// 		this.pageViewData.customer_first_name = customerData.firstName;
				// 		this.pageViewData.customer_last_name = customerData.lastName;
				// 		this.pageViewData.customer_phone = customerData.mobile;
				// 		this.pageViewData.customer_phone_local = customerData.mobile;

				// 		if (addressValue) {
				// 			this.pageViewData.customer_address = addressValue.composite;
				// 			this.pageViewData.customer_city = addressValue.suburb;
				// 			this.pageViewData.customer_state = addressValue.state;
				// 			this.pageViewData.customer_postcode = addressValue.postcode;
				// 			this.pageViewData.customer_country = DefaultValues.customer_country;
				// 			this.pageViewData.customer_street_address = customerData.mobileAddress;
				// 			if (addressValue.gnafId) {
				// 				this.pageViewData.customer_gnafid = addressValue.gnafId;
				// 			}
				// 		}
				// 	}

				// 	this.pageViewData.page_id = Constants.page_id_thank_you;
				// 	this.pageViewData.billing_frequency =
				// 		DefaultValues.billing_frequency;
				// 	this.pageViewData.order_currency_code =
				// 		DefaultValues.order_currency_code;
				// 	this.pageViewData.order_shipping_type =
				// 		DefaultValues.order_shipping_type;
				// 	this.pageViewData.order_shipping_amount =
				// 		Constants.shipping_amount;
				// 	this.pageViewData.order_subtotal =
				// 		ProductDetails.formatPrice(this.order_subtotal);
				// 	this.pageViewData.order_total = ProductDetails.formatPrice(
				// 		this.order_total
				// 	);
				// 	this.pageViewData.order_tax_amount =
				// 		ProductDetails.formatPrice(
				// 			this.order_total - this.order_subtotal
				// 		);
				// 	if (this.checkoutUserData) {
				// 		this.pageViewData.order_payment_type = this
				// 			.checkoutUserData.paymentDetails
				// 			? this.checkoutUserData.paymentDetails
				// 				.paymentCardType
				// 			: DefaultValues.order_payment_type;
				// 	}
				// 	this.pageViewData.order_store = DefaultValues.order_store;

				// 	Object.assign(this.pageViewData, this.cartData);
				// 	Object.assign(this.pageViewData, {
				// 		product_category:
				// 			this.pageViewData.cart_products_category,
				// 		product_subcategory:
				// 			this.pageViewData.cart_products_subcategory,
				// 	});
				// 	this.addOrderPromoCode();
				// 	const isSalesFlowTransition =
				// 		this._localStorageService.getFromSessionStorage(
				// 			LocalStorageKeys.isSalesFlowTransition
				// 		);
				// 	if (!!isSalesFlowTransition) {
				// 		this.pageViewData.cart_products_previous_subcategory =
				// 			[];
				// 	}
				// 	const page_heading =
				// 		this._localStorageService.getFromSessionStorage(
				// 			LocalStorageKeys.thankyouPageHeading
				// 		);
				// 	if (!!page_heading) {
				// 		this.pageViewData.page_heading = page_heading;
				// 	}

				// 	// Clear the cart data
				// 	this.cartData = null;
				// 	this._localStorageService.deleteFromSessionStorage(
				// 		Constants.session_storage_key
				// 	);
				// 	break;
				case "/checkout/error":
					this.pageViewData.page_id =
						Constants.page_id_checkout_error;
					if (this.checkoutError) {
						this.pageViewData.error_code =
							this.checkoutError.errorCode;
						this.pageViewData.error_description =
							this.checkoutError.title;
					}
					break;
				case "/error":
					this.pageViewData.page_id = Constants.page_id_error;
					break;
				case "/payment-details":
					this.pageViewData.dealer_reference_id = this._refId;
			}

			window.utag_data = Object.assign(
				window.utag_data,
				this.pageViewData
			);
		} catch (error) {
			this.pageViewData.error_description =
				error["message"] || "error occurred in page view tracking";
		} finally {
			// Clean out all unset page view data from utag_data (leaving any other utag_data fields set by Tealium or other services)
			// Object.keys(this.pageViewData).forEach(
			// 	(key) =>
			// 		this.pageViewData[key] === null &&
			// 		window.utag_data[key] &&
			// 		delete window.utag_data[key]
			// );
			// Clean the page data for Tealium (removes any attributes that are null and formats the data for Tealium)
			this.pageViewData = TealiumService.formatDataForTealium(
				this.pageViewData
			);
			this.view(this.pageViewData);
		}
	}

	trackPageEvent(data: object) {
		if (true === data["disabled"]) {
			return;
		}
		delete data["disabled"];

		let eventData = {
			tealium_event: TealiumService.formatValueForTealium(
				data["tealium_event"] || "sales_funnel_interaction"
			),
		};

		if (this.dealer) {
			eventData = Object.assign(eventData, {
				dealer_name: this.dealer.dealerName,
			});
		}

		if (this.lastProductView) {
			eventData = Object.assign(
				eventData,
				this.getProductData(
					this.__getProductDetail(this.lastProductView)
				)
			);
		}

		eventData = Object.assign(eventData, data);
		try {
			let eventDataDetails = {};

			switch (data["tealium_event"]) {
				case "send_quote":
					break;
				case "error_page":
					break;
			}

			Object.assign(eventData, eventDataDetails);
		} catch (error) {
			Object.assign(eventData, {
				error_description:
					error["message"] || "error occurred in event tracking",
			});
		} finally {
			eventData = TealiumService.formatDataForTealium(eventData);
			this.link(Object.assign({}, eventData));
		}
	}

	private __getProductDetail(productName: string): ProductDetails {
		switch (productName?.toLowerCase()) {
			case "mobile":
				return this.mobileProductDetails;
			default:
				return null;
		}
	}

	private getProductData(productDetails: ProductDetails = null) {
		if (!productDetails) {
			return {
				product_brand: [],
				product_category: [],
				product_subcategory: [],
				product_id: [],
				product_name: [],
				product_original_price: [],
				product_price: [],
				product_promo_code: [],
				product_quantity: [],
				product_discount: [],
			};
		}

		if (
			!productDetails.monthlyFee ||
			!productDetails.upfrontCost ||
			productDetails.monthlyFee === 0 ||
			productDetails.upfrontCost === 0
		) {
			return this.__getProductData(productDetails);
		}

		const productData = this.__getProductData(
			productDetails,
			productDetails.monthlyFee
		);
		this.addToObject(
			productData,
			this.__getProductData(productDetails, productDetails.upfrontCost)
		);
		return productData;
	}

	private __getProductData(
		productDetails: ProductDetails,
		productTotal: number = null
	) {
		const isProductSelected = !!productDetails.id;

		return {
			product_brand: [DefaultValues.product_brand],
			product_category: [productDetails.category],
			product_subcategory: productDetails.subcategory
				? [productDetails.subcategory]
				: null,
			product_name: isProductSelected ? [productDetails.name] : null,
			product_id: isProductSelected ? [productDetails.id] : null,
			// product_sku: isProductSelected
			// ? [ TealiumService.isProductSelected("") ]
			// : null,
			product_original_price: isProductSelected
				? productTotal
					? [productTotal]
					: [ProductDetails.formatPrice(productDetails.originalPrice)]
				: null,
			product_price: isProductSelected
				? productTotal
					? [productTotal]
					: [ProductDetails.formatPrice(productDetails.total)]
				: null,
			product_promo_code: isProductSelected
				? [productDetails.promoCode || ""]
				: null,
			product_quantity: isProductSelected ? ["1"] : null,
			product_discount: isProductSelected
				? [ProductDetails.formatPrice(productDetails.discount)]
				: null,
		};
	}

	private addToObject(targetObject: any, additionObject: any) {
		if (!targetObject || !additionObject) {
			return;
		}

		Object.keys(targetObject).forEach((key) => {
			targetObject[key] = targetObject[key].concat(
				additionObject[key] ? additionObject[key] : null
			);
		});
	}

	private addProductToCart(productData: any = null) {
		if (!productData) {
			return;
		}

		Object.keys(productData).forEach((key) => {
			if (productData[key]) {
				this.cartData[key.replace("product_", "cart_products_")] =
					this.cartData[
						key.replace("product_", "cart_products_")
					].concat(productData[key]);
			}
		});
	}

	private addOrderPromoCode() {
		if (this.pageViewData.cart_products_promo_code) {
			this.pageViewData.order_promo_code = [];
			this.pageViewData.cart_products_promo_code.forEach((element) => {
				if (element && element.length > 0) {
					this.pageViewData.order_promo_code.push(element);
				}
			});
		}

		// targetted promotion
		const targettedPromoCode =
			this._localStorageService.getFromSessionStorage(
				LocalStorageKeys.promoDiscountCode
			);
		if (targettedPromoCode) {
			this.pageViewData.order_promo_code.push(targettedPromoCode);
		}
	}

	trackPurchaseEvent(orderId: string): void {
		const FIRED_KEY = "submit_order_fired";
		if (this._localStorageService.getFromSessionStorage(FIRED_KEY)) {
			return;
		}
		this._localStorageService.setInSessionStorage(FIRED_KEY, "1");

		const checkout = JSON.parse(this._localStorageService.getFromSessionStorage("checkout") || "null");
		const aboutYou = checkout?.steps?.aboutYou;
		const addressValue = aboutYou?.mobileAddressValue;

		const cart = JSON.parse(this._localStorageService.getFromSessionStorage("cart") || "null");
		const mobileCart = cart?.products?.mobile;

		const plan = JSON.parse(this._localStorageService.getFromSessionStorage(LocalStorageKeys.plan) || "null");
		const selectedPlan = plan?.mobile?.selectedPlan;
		const productDiscount = selectedPlan?.discount;
		const productId = plan?.mobile?.productOffering?.[0]?.productOffering?.[0]?.id || "";

		const sessionPromoCode = this._localStorageService.getFromSessionStorage(LocalStorageKeys.promoDiscountCode) || "";
		const validatedPromo = this._localStorageService.getFromSessionStorage(LocalStorageKeys.validatedPromo);
		const promoCode = selectedPlan?.discount?.promotionName
			|| selectedPlan?.discount?.name
			|| sessionPromoCode
			|| "";

		let discountValue: any = "";
		if (productDiscount) {
			discountValue = productDiscount.isData
				? productDiscount.data
				: productDiscount.isPercent
				? productDiscount.percentage
				: productDiscount.isFixedAmount
				? productDiscount.fixedAmount
				: "";
		}

		let productName = "";
		const title = mobileCart?.details?.title || "";
		if (validatedPromo) {
			const parsed = typeof validatedPromo === "string" ? JSON.parse(validatedPromo) : validatedPromo;
			const startDate = moment(parsed?.validFor?.startDateTime?.split("T")?.[0]).format("MM/YYYY");
			const endDate = moment(parsed?.validFor?.endDateTime?.split("T")?.[0]).format("MM/YYYY");
			productName = `${Constants.category_mobile} + ${title} + ${startDate} to ${endDate}`;
		} else {
			productName = `${Constants.category_mobile} + ${title}`;
		}

		const payload = TealiumService.formatDataForTealium({
			billing_frequency: DefaultValues.billing_frequency,
			cart_products_brand: [DefaultValues.product_brand],
			cart_products_category: [Constants.category_mobile],
			cart_products_discount: [discountValue != null ? String(discountValue) : ""],
			cart_products_id: [productId],
			cart_products_name: [productName],
			cart_products_original_price: [mobileCart?.monthlyCost != null ? String(mobileCart.monthlyCost) : ""],
			cart_products_price: [mobileCart?.dueToday != null ? String(mobileCart.dueToday) : ""],
			cart_products_promo_code: promoCode ? [promoCode, ""] : [""],
			cart_products_quantity: ["1"],
			cart_products_subcategory: [Constants.subcategory_postpaid],

			customer_address: addressValue?.composite || "",
			customer_city: addressValue?.suburb || "",
			customer_country: DefaultValues.customer_country,
			customer_dob: aboutYou?.dob || "",
			customer_email: aboutYou?.email || "",
			customer_first_name: aboutYou?.firstName || "",
			customer_last_name: aboutYou?.lastName || "",
			customer_postcode: addressValue?.postcode || "",
			customer_state: addressValue?.state || "",
			customer_street_address: aboutYou?.mobileAddress || "",

			event_trigger: this.isMobileFlow ? "Signup Confirmation" : "SIM Order",

			order_currency_code: DefaultValues.order_currency_code,
			order_id: orderId || "",
			order_promo_code: promoCode ? [promoCode] : [""],
			order_shipping_amount: "0",
			order_shipping_type: DefaultValues.order_shipping_type,
			order_store: DefaultValues.order_store,
			order_subtotal: ProductDetails.formatPrice(ProductDetails.beforeTax(mobileCart?.dueToday ?? 0)),
			order_tax_amount: ProductDetails.formatPrice((mobileCart?.dueToday ?? 0) - ProductDetails.beforeTax(mobileCart?.dueToday ?? 0)),
			order_total: [mobileCart?.dueToday != null ? String(mobileCart.dueToday) : ""],

			product_category: [Constants.category_mobile],
			product_subcategory: [Constants.subcategory_postpaid],

			tealium_event: this.isMobileFlow ? "order_confirmation_view" : "submit_order",
		});

		this.view(payload);
	}

	// Data layer is optional set of key/value pairs
	track(tealium_event: string, data?: any) {
		try {
			(<any>window).utag.track(tealium_event, data);
		} catch {
			console.log("Tealium:: utag exception.");
		}
	}

	view(data?: any) {
		this.track("view", data);
	}

	link(data?: any) {
		this.track("link", data);
	}

	private populateCartProductsData(): void {
		const plan = JSON.parse(this._localStorageService.getFromSessionStorage(LocalStorageKeys.plan));
		const cart = JSON.parse(this._localStorageService.getFromSessionStorage("cart"));
		if (!cart) return;

		const simType = (isOrderESIM(cart?.products?.currentProduct) || isAuthenticatedOrdereSIM(cart?.products?.currentProduct)) ? "eSim" : "physical";

		this.pageViewData.cart_products_brand = ["Dodo"];
		this.pageViewData.cart_products_category = [Constants.category_mobile];

		if (plan) {
			this.pageViewData.cart_products_id = [plan.mobile.productOffering[0].productOffering[0].id];
		}

		const product_discount = plan?.mobile?.selectedPlan?.discount;
		if (product_discount) {
			const discount_value = product_discount.isData ? product_discount.data : product_discount.isPercent ? product_discount.percentage : product_discount.isFixedAmount ? product_discount.fixedAmount : 0;
			this.pageViewData.cart_products_discount = [discount_value];
		}

		const validated_promo = this._localStorageService.getFromSessionStorage(LocalStorageKeys.validatedPromo);
		if (validated_promo) {
			const start_date = moment(validated_promo.validFor.startDateTime.split("T")[0]).format('MM/YYYY');
			const end_date = moment(validated_promo.validFor.endDateTime.split("T")[0]).format('MM/YYYY');
			const title = cart.products.mobile.details.title;
			this.pageViewData.cart_products_name = [Constants.category_mobile + " + " + title + " + " + start_date + " to " + end_date + " " + simType];
		} else {
			const title = cart.products.mobile.details.title;
			this.pageViewData.cart_products_name = [Constants.category_mobile + " + " + title + " " + simType];
		}

		this.pageViewData.cart_products_original_price = [cart.products.mobile.monthlyCost];
		this.pageViewData.cart_products_price = [cart.products.mobile.dueToday];

		const promo_code = this._localStorageService.getFromSessionStorage(LocalStorageKeys.promoDiscountCode);
		if (promo_code) {
			this.pageViewData.cart_products_promo_code = [promo_code];
		}

		this.pageViewData.cart_products_quantity = ["1"];
		this.pageViewData.cart_products_subcategory = ["Mobile Postpaid"];
	}

	public ngOnDestroy() {
		this._unsubscribeAll.next(void 0);
		this._unsubscribeAll.complete();
	}
}
